import { Fragment } from "react"
import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"

import { cn } from "~/utils/helpers"
import ChatActionCard from "./ChatActionCard"
import ChatChartBlock from "./ChatChartBlock"
import ChatMatrixBlock from "./ChatMatrixBlock"
import ChatMetricBlock from "./ChatMetricBlock"
import ChatTableBlock from "./ChatTableBlock"
import ChatUnavailableBlock from "./ChatUnavailableBlock"
import CopyableTable from "./CopyableTable"
import MarkdownLink from "./MarkdownLink"
import type { CommittedBlock } from "~/hooks/useHakiStream"
import { CopyableCode } from "~/components/design-system/components"
import { readMarkdownCode } from "~/utils/markdownCode"

interface ChatMarkdownProps {
  content: string
  blocks?: CommittedBlock[]
  compact?: boolean
  streaming?: boolean
  threadId?: number
  messageId?: number
}

const BUILDING_LABELS = {
  chart: "Building chart...",
  table: "Building table...",
  kpis: "Building KPIs...",
}

function isParsableJson(raw: string): boolean {
  try {
    JSON.parse(raw)
    return true
  } catch {
    return false
  }
}

type ContentSegment =
  | { type: "text"; content: string }
  | { type: "block"; block: CommittedBlock & { afterLength: number } }

interface SegmentRenderOptions {
  compact?: boolean
  threadId?: number
  messageId?: number
  components: Components
}

function renderSegment(segment: ContentSegment, options: SegmentRenderOptions): React.ReactNode {
  if (segment.type === "text") {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={options.components}>
        {segment.content}
      </ReactMarkdown>
    )
  }

  const { block } = segment
  switch (block.blockType) {
    case "chart":
      return <ChatChartBlock validated={block.data} />
    case "table":
      return <ChatTableBlock validated={block.data} compact={options.compact} />
    case "matrix":
      return <ChatMatrixBlock validated={block.data} compact={options.compact} />
    case "unavailable":
      return <ChatUnavailableBlock validated={block.data} />
    case "action":
      return (
        <ChatActionCard
          blockId={block.blockId}
          validated={block.data}
          resolution={block.resolution}
          threadId={options.threadId}
          messageId={options.messageId}
        />
      )
    default:
      return <ChatMetricBlock validated={block.data} />
  }
}

function splitContentByToolBlocks(
  content: string,
  blocks: CommittedBlock[] | undefined,
): ContentSegment[] {
  const toolBlocks = (blocks ?? [])
    .filter((b): b is CommittedBlock & { afterLength: number } => b.afterLength != null)
    .sort((a, b) => a.afterLength - b.afterLength)
  if (toolBlocks.length === 0) return [{ type: "text", content }]

  const segments: ContentSegment[] = []
  let cursor = 0
  for (const block of toolBlocks) {
    const cut = Math.min(Math.max(block.afterLength, cursor), content.length)
    segments.push({ type: "text", content: content.slice(cursor, cut) })
    segments.push({ type: "block", block })
    cursor = cut
  }
  segments.push({ type: "text", content: content.slice(cursor) })
  return segments
}

export default function ChatMarkdown({
  content,
  blocks,
  compact,
  streaming,
  threadId,
  messageId,
}: ChatMarkdownProps) {
  // Build a lookup map for validated chart blocks by blockId
  const blockMap = new Map(blocks?.map((b) => [b.blockId, b]) ?? [])
  const segments = splitContentByToolBlocks(content, blocks)

  const components: Components = {
    h1: ({ children }) => <h1 className="label-lg mt-4 mb-1">{children}</h1>,
    h2: ({ children }) => <h2 className="label-lg mt-4 mb-1">{children}</h2>,
    h3: ({ children }) => <h3 className="label-base mt-3 mb-1">{children}</h3>,
    h4: ({ children }) => <h4 className="label-base mt-3 mb-1">{children}</h4>,
    ul: ({ children }) => <ul className="my-2 ml-4 list-disc space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="my-2 ml-4 list-decimal space-y-1">{children}</ol>,
    li: ({ children }) => <li className="pl-1">{children}</li>,
    table: ({ children }) => <CopyableTable compact={compact}>{children}</CopyableTable>,
    thead: ({ children }) => (
      <thead className="bg-medium text-fg-subtle sticky top-0">{children}</thead>
    ),
    th: ({ children }) => (
      <th className="label-base text-left align-middle px-2 py-[5px] first:rounded-l-[8px] last:rounded-r-[8px] whitespace-nowrap">
        {children}
      </th>
    ),
    tr: ({ children }) => <tr className="hover:bg-base-hover transition-colors">{children}</tr>,
    td: ({ children }) => (
      <td className="px-2 py-1 align-middle first:rounded-l-[8px] last:rounded-r-[8px] whitespace-nowrap">
        {children}
      </td>
    ),
    pre: ({ children }) => {
      const { code, language } = readMarkdownCode(children)
      const raw = code.trim()
      const fallback = <CopyableCode code={code} language={language} size="sm" />
      if (language !== "chart" && language !== "table" && language !== "kpis") return fallback
      // During streaming, the block is complete once its JSON parses
      if (streaming && !isParsableJson(raw)) {
        return (
          <div className="my-2 rounded-lg bg-medium p-4 text-center text-fg-muted text-sm animate-pulse">
            {BUILDING_LABELS[language]}
          </div>
        )
      }
      if (language === "chart") return <ChatChartBlock raw={raw} />
      if (!isParsableJson(raw)) return fallback
      if (language === "table") {
        return <ChatTableBlock validated={JSON.parse(raw)} compact={compact} />
      }
      return <ChatMetricBlock validated={JSON.parse(raw)} />
    },
    p: ({ children }) => {
      // Render validated chart/number/table blocks from placeholders
      if (typeof children === "string") {
        const chartMatch = children.match(/^\[chart:(blk_\S+)\]$/)
        if (chartMatch) {
          const block = blockMap.get(chartMatch[1]!)
          if (block) return <ChatChartBlock validated={block.data} />
        }
        const tableMatch = children.match(/^\[table:(blk_\S+)\]$/)
        if (tableMatch) {
          const block = blockMap.get(tableMatch[1]!)
          if (block) return <ChatTableBlock validated={block.data} compact={compact} />
        }
        const kpisMatch = children.match(/^\[kpis:(blk_\S+)\]$/)
        if (kpisMatch) {
          const block = blockMap.get(kpisMatch[1]!)
          if (block) return <ChatMetricBlock validated={block.data} />
        }
      }
      return <p className="my-2">{children}</p>
    },
    a: ({ href, children }) => <MarkdownLink href={href ?? ""}>{children}</MarkdownLink>,
  }

  return (
    <div
      className={cn(
        "prose max-w-none dark:prose-invert overflow-hidden paragraph-base space-y-3 [&>*:first-child]:mt-0",
        compact && "paragraph-base [&_img]:max-h-48 [&_img]:w-auto",
      )}
    >
      {segments.map((segment, i) => (
        <Fragment key={i}>
          {renderSegment(segment, { compact, threadId, messageId, components })}
        </Fragment>
      ))}
    </div>
  )
}
